import { useEffect, useState } from 'react';
import { addDownloadFile, getActiveUserInfo } from '../../utils/localStore.util';
import { ApiResponse, DocumentItem, ScheduleEntry, isExcel, isPdf } from './schedule.types';

export const useSchedule = () => {
    const user = getActiveUserInfo();
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [currentDate, setCurrentDate] = useState(new Date());
    const [scheduleData, setScheduleData] = useState<Record<string, ScheduleEntry[]>>({});
    const [loading, setLoading] = useState(false);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedCoordinator, setSelectedCoordinator] = useState<ScheduleEntry | null>(null);
    const [selectedDoc, setSelectedDoc] = useState<DocumentItem | null>(null);
    const [previewLoaded, setPreviewLoaded] = useState(false);

    useEffect(() => { fetchSchedule(); }, []);
    useEffect(() => { setPreviewLoaded(false); }, [selectedDoc?.id]);

    const fetchSchedule = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${BASE_URL}coordinators/list`, {
                headers: {
                    'Content-Type': 'application/json',
                    token: `${user?.api_token}`,
                },
            });
            const data: ApiResponse = await response.json();
            setScheduleData(data?.data || {});
        } catch (error) {
            console.error('Schedule fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDateClick = (dateKey: string, coordinators: ScheduleEntry[]) => {
        if (coordinators.length === 0) return;
        setSelectedDate(dateKey);
        setSelectedCoordinator(null);
        setSelectedDoc(null);
        setSidebarOpen(true);
    };

    const handleCoordinatorClick = (entry: ScheduleEntry) => {
        if (selectedCoordinator?.id === entry.id) {
            setSelectedCoordinator(null);
            setSelectedDoc(null);
        } else {
            setSelectedCoordinator(entry);
            setSelectedDoc(null);
        }
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
        setSelectedDate(null);
        setSelectedCoordinator(null);
        setSelectedDoc(null);
    };

    const handleDownload = async (doc: DocumentItem) => {
        try {
            const extension = isPdf(doc.file_type) ? 'pdf'
                : isExcel(doc.file_type) ? 'xlsx'
                    : doc.file_type;

            const filename = `${doc.title}.${extension}`;

            // Tamara Laravel proxy endpoint use karo
            const proxyUrl = `${BASE_URL}download-file?url=${encodeURIComponent(doc.file_url)}`;

            const response = await fetch(proxyUrl, {
                headers: { token: `${user?.api_token}` }
            });

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);

            addDownloadFile({
                id: String(doc.id),
                title: doc.title,
                url: doc.file_url,
                downloadedAt: new Date().toISOString(),
                file_type: isPdf(doc.file_type) ? 'pdf' : isExcel(doc.file_type) ? 'excel' : undefined,
            });
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback
            window.open(doc.file_url, '_blank', 'noopener,noreferrer');
        }
    };

    return {
        currentDate,
        setCurrentDate,
        scheduleData,
        loading,
        sidebarOpen,
        selectedDate,
        selectedCoordinator,
        selectedDoc,
        setSelectedDoc,
        previewLoaded,
        setPreviewLoaded,
        handleDateClick,
        handleCoordinatorClick,
        closeSidebar,
        handleDownload,
    };
};